import React, { useState, useEffect } from "react";
import payrollService from "../../services/payrollService";
import PayrollCard from "../../components/Payroll/PayrollCard";
import Notification from "../../components/UI/Notification";
import {
  Users,
  DollarSign,
  FileText,
  TrendingUp,
  RefreshCw,
  Download,
} from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const NewDashboard = () => {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, chartsRes, activitiesRes, summaryRes] =
        await Promise.all([
          payrollService.getDashboardStats(),
          payrollService.getDashboardCharts(),
          payrollService.getRecentActivities(),
          payrollService.getExecutiveSummary(),
        ]);

      if (statsRes.success) setStats(statsRes.data);
      if (chartsRes.success) setCharts(chartsRes.data);
      if (activitiesRes.success) setRecentActivities(activitiesRes.data);
      if (summaryRes.success) setExecutiveSummary(summaryRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setNotification({
        type: "error",
        message: "Failed to load dashboard data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleExport = () => {
    setNotification({
      type: "info",
      message: "Export feature coming soon!",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      processed:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      approved:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      paid: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
      rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    };
    return colors[status] || colors.draft;
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Payroll Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Overview of payroll operations and statistics
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
          >
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <PayrollCard
          title="Total Records"
          value={stats?.current_month?.total_records || 0}
          subtitle={`${stats?.current_month?.processed_count || 0} processed`}
          icon={<FileText className="text-blue-600" size={24} />}
          trend={
            stats?.comparison?.records_change_percentage > 0 ? "up" : "down"
          }
          trendValue={`${Math.abs(
            stats?.comparison?.records_change_percentage || 0
          )}%`}
          loading={loading}
        />
        <PayrollCard
          title="Total Payroll Amount"
          value={`₹${(
            stats?.current_month?.total_payroll_amount || 0
          ).toLocaleString("en-IN")}`}
          subtitle="Current month"
          icon={<DollarSign className="text-green-600" size={24} />}
          trend={
            stats?.comparison?.payroll_change_percentage > 0 ? "up" : "down"
          }
          trendValue={`${Math.abs(
            stats?.comparison?.payroll_change_percentage || 0
          )}%`}
          loading={loading}
        />
        <PayrollCard
          title="Total Employees"
          value={stats?.total_employees || 0}
          subtitle="Active employees"
          icon={<Users className="text-purple-600" size={24} />}
          loading={loading}
        />
        <PayrollCard
          title="Approved Records"
          value={stats?.current_month?.approved_count || 0}
          subtitle={`${stats?.current_month?.paid_count || 0} paid`}
          icon={<TrendingUp className="text-orange-600" size={24} />}
          loading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Monthly Trend Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Monthly Payroll Trend
          </h3>
          {charts?.monthly_trend && (
            <Line
              data={{
                labels: charts.monthly_trend.months,
                datasets: [
                  {
                    label: "Payroll Amount",
                    data: charts.monthly_trend.payroll_amounts,
                    borderColor: "rgb(59, 130, 246)",
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                    tension: 0.4,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: true,
                    position: "top",
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `₹${(value / 1000).toFixed(0)}K`,
                    },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Department Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Department Distribution
          </h3>
          {charts?.department_distribution && (
            <Bar
              data={{
                labels: charts.department_distribution.map((d) => d.department),
                datasets: [
                  {
                    label: "Amount",
                    data: charts.department_distribution.map((d) => d.amount),
                    backgroundColor: "rgba(59, 130, 246, 0.6)",
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `₹${(value / 1000).toFixed(0)}K`,
                    },
                  },
                },
              }}
            />
          )}
        </div>

        {/* Status Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Status Distribution
          </h3>
          {charts?.status_distribution && (
            <Pie
              data={{
                labels: charts.status_distribution.map((s) => s.status),
                datasets: [
                  {
                    data: charts.status_distribution.map((s) => s.count),
                    backgroundColor: [
                      "rgba(59, 130, 246, 0.8)",
                      "rgba(34, 197, 94, 0.8)",
                      "rgba(168, 85, 247, 0.8)",
                      "rgba(251, 146, 60, 0.8)",
                      "rgba(239, 68, 68, 0.8)",
                    ],
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: "right",
                  },
                },
              }}
            />
          )}
        </div>

        {/* Executive Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Yearly Summary
          </h3>
          {executiveSummary?.yearly_summary && (
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Total Employees
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {executiveSummary.yearly_summary.total_employees}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Total Payroll
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  ₹
                  {executiveSummary.yearly_summary.total_payroll?.toLocaleString(
                    "en-IN"
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Average Salary
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  ₹
                  {executiveSummary.yearly_summary.average_salary?.toLocaleString(
                    "en-IN"
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Paid Records
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {executiveSummary.yearly_summary.paid_records} /{" "}
                  {executiveSummary.yearly_summary.total_records}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Recent Activities
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Net Salary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Processed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <tr key={activity.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {activity.employee_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {activity.payroll_period}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                          activity.status
                        )}`}
                      >
                        {activity.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                      ₹{activity.net_salary?.toLocaleString("en-IN")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {activity.processed_by}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {activity.updated_at}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    No recent activities found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NewDashboard;
